import type { FetcherWithComponents } from "react-router";
import { useFormAction, useNavigation } from "react-router";

export interface IFormActionOptions {
  fetcher?: FetcherWithComponents<unknown>;
  formAction?: string | null;
  formMethod?: "POST" | "GET" | "PUT" | "PATCH" | "DELETE";
  formDataMatches?: Record<string, string>;
}

/**
 * Custom hook to determine if a form submission is currently in progress.
 * Checks if the current navigation state or fetcher (if provided) is either "loading" or "submitting",
 * matching the specified form action and method.
 *
 * @param options - Configuration options for the hook.
 * @param [options.fetcher] - Optional fetcher object that monitors a specific fetch request.
 * @param [options.formAction] - Specific form action URL to match against; if `null`, any matching form action with the specified method is considered.
 * @param [options.formMethod="POST"] - HTTP method to match against; defaults to "POST".
 * @param [options.submitAction] - Optional `action` parameter in the form data to match against.
 *
 * @returns `true` if the current navigation or fetcher is in a submitting or loading state and matches the specified criteria; otherwise `false`.
 */
export function useIsSubmitting({
  fetcher,
  formAction,
  formMethod = "POST",
  formDataMatches,
}: IFormActionOptions = {}) {
  const contextualFormAction = useFormAction();
  const navigation = useNavigation();

  const contexts = fetcher ? [fetcher, navigation] : [navigation];

  return contexts.some((context) => {
    return (
      ["loading", "submitting"].includes(context.state) &&
      (formAction === null ||
        context.formAction === (formAction ?? contextualFormAction)) &&
      context.formMethod === formMethod &&
      (!formDataMatches ||
        Object.entries(formDataMatches).every(
          ([key, value]) => context.formData?.get(key) === value,
        ))
    );
  });
}
